import { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '../components/common/PageHeader';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LineChart, Line, Tooltip } from 'recharts';
import { useProject } from '../contexts/ProjectContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useNetworkEnabled } from '../hooks/useNetworkEnabled';
import { shouldFetchNextPage } from '../services/paging';
import StageModelsWidget from '../components/common/StageModelsWidget';
import api from '../services/api';
import AddToTopicModal from '../components/AddToTopicModal';
import SerpModal from '../components/SerpModal';
import StoriesWorthAPage from '../components/trends/StoriesWorthAPage';
import StartSubjectModal from '../components/trends/StartSubjectModal';
import { reconName, reconQuestion, reconRoomPath } from '../components/trends/reconFromKeywords';
import { useShellNavigate } from '../components/common/ShellLink';
import { useEntitlements } from '../contexts/EntitlementContext';
import { apiErrorMessage } from '../services/errorMessage';
import {
  ageBucket,
  ageChipClass,
  ageLabel,
  fadeClass,
  DEFAULT_FRESHNESS_DAYS,
  FRESHNESS_WINDOWS,
  arrivedFiltered,
  emptyMessage,
  emptyReason,
  resolveTrendsTab,
  trendsTabPath,
} from './trendFreshness';
import type { TrendsTab } from './trendFreshness';
import { EngineIcon, ExternalSearchLink, RefreshGlyph, formatSerpDate } from '../components/common/SerpHelpers';
import { LoadingRegion } from '../components/common/icons';
import type { TrendingKeyword, RecommendedTopicLink, SerpGapFilter, WebSocketEvent } from '../types';
import {
  EMPTY_GAP_FILTER,
  SERP_GAP_HELP,
  SerpGapBadge,
  SerpGapFilterFields,
  gapFilterActive,
} from '../components/topic-keywords/SerpGap';

type SortField = 'growth' | 'avg_monthly_searches' | 'keyword' | 'recent_avg' | 'similarity';

const DEFAULT_MIN_SEM_SIMILARITY = 0;

export default function Trending() {
  const { projects } = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [items, setItems] = useState<TrendingKeyword[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const pageRef = useRef(1);
  const [sortBy, setSortBy] = useState<SortField>('growth');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterProjectId, setFilterProjectId] = useState<number | null>(null);
  // 'explored' = every keyword the user's Google Ads pulls returned. These have
  // no Project link, so they're reached via this scope rather than the project
  // filter.
  const initialScope = searchParams.get('scope');
  const [scope, setScope] = useState<'explored' | null>(
    initialScope === 'explored' ? 'explored' : null,
  );
  // Within 'explored': a seed narrows to Google's expansion of that concept (what
  // Keyword Planner associated with it). Populated from the user's pulls.
  const [seedFilter, setSeedFilter] = useState<string | null>(
    searchParams.get('seed') || null,
  );
  const [exploredSeeds, setExploredSeeds] = useState<
    { seed: string; keyword_count: number }[]
  >([]);
  const [minGrowth, setMinGrowth] = useState<string>('');
  const [minVolume, setMinVolume] = useState<string>('');
  // The top-10 gap filter. Applies as you type, like Min Volume beside it.
  const [gapFilter, setGapFilter] = useState<SerpGapFilter>(EMPTY_GAP_FILTER);
  // THE RECENCY SCOPE. Without it the table presents a growth figure pulled
  // four months ago as today's news. Defaults to 30 days to match the backend's
  // DAYS_BETWEEN_RETRY_OF_KEYWORD — anything shorter hides keywords that are
  // merely inside their cache window and reads as "you researched nothing".
  const [freshnessDays, setFreshnessDays] = useState<number | null>(DEFAULT_FRESHNESS_DAYS);
  // The old filter bar dominated a page whose point is now the cards above it,
  // so it starts collapsed — UNLESS the URL arrived carrying a filter, in which
  // case hiding it would show a filtered table with no visible reason.
  const [showFilters, setShowFilters] = useState(() => arrivedFiltered(searchParams));
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const { held } = useEntitlements();
  const shellNavigate = useShellNavigate();
  const [startingRecon, setStartingRecon] = useState(false);
  const [reconError, setReconError] = useState<string | null>(null);
  const [filedFilter, setFiledFilter] = useState<'any' | 'unfiled' | 'filed'>('any');
  const [pageFilter, setPageFilter] = useState<'any' | 'yes' | 'no'>('any');

  /* TWO TABS, not two stacked sections. They are different datasets answering
     different questions — "what is the country searching" and "what have I
     researched" — and stacked, the second was permanently below the fold while
     the first grew a five-day back catalogue above it.

     The tab lives in the URL so it survives a reload, can be linked to, and can
     be SELECTED BY a link: every deep-link that filters the keyword table now
     lands on the keyword table. */
  const tab: TrendsTab = resolveTrendsTab(searchParams, location.pathname);

  // ARRIVE AT THE RIGHT ADDRESS, not merely at the right table.
  //
  // This also took over from `keepReturnTo`, which the old tab switcher used to
  // carry a return trail across a tab change: the whole query string rides
  // along here, `from` and `from_label` with it. Moving between the two lists
  // by the NAV drops the trail, which is right — the nav is the app's spine,
  // and a trail belongs to the deep link that made it.
  //
  // Four links in the app carry keyword filters and every one of them points at
  // `/trending` — the brainstorm room's `?scope=explored&seed=…`, the
  // Dashboard's `?recommendations_only=true` and `?keyword_search=…`, and the
  // `/recommendations` redirect. They resolve to the keyword list, which is
  // right; but now that the two lists are nav entries, showing it under the
  // other entry's highlight is the same bug as filtering a table you cannot
  // see. So the URL is corrected to match what is on screen, keeping every
  // param — including the return trail.
  useEffect(() => {
    const wanted = trendsTabPath(tab);
    if (location.pathname === wanted) return;
    navigate(
      { pathname: wanted, search: location.search },
      { replace: true, state: location.state },
    );
  }, [tab, location.pathname, location.search, location.state, navigate]);

  // Text & semantic search state
  const initialKeywordSearch = searchParams.get('keyword_search') || '';
  const [textFilter, setTextFilter] = useState(initialKeywordSearch);
  const [appliedTextFilter, setAppliedTextFilter] = useState(initialKeywordSearch);
  const [semanticQuery, setSemanticQuery] = useState('');
  const [appliedSemanticQuery, setAppliedSemanticQuery] = useState('');
  const [minSemanticSimilarity, setMinSemanticSimilarity] = useState(DEFAULT_MIN_SEM_SIMILARITY);
  const [appliedMinSemanticSimilarity, setAppliedMinSemanticSimilarity] = useState(DEFAULT_MIN_SEM_SIMILARITY);

  const [recommendationsOnly, setRecommendationsOnly] = useState(searchParams.get('recommendations_only') === 'true');
  const [modalKeyword, setModalKeyword] = useState<TrendingKeyword | null>(null);
  const [recModal, setRecModal] = useState<{ keyword: TrendingKeyword; rec: RecommendedTopicLink } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [serpModalKeyword, setSerpModalKeyword] = useState<{ id: number; value: string } | null>(null);
  const [requestingSerpFor, setRequestingSerpFor] = useState<number | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { subscribe } = useWebSocket();
  const networkEnabled = useNetworkEnabled();

  const handleRequestSerp = async (keywordId: number) => {
    if (!networkEnabled) return;
    setRequestingSerpFor(keywordId);
    try {
      await api.createSerpRequest(keywordId);
      setItems(prev =>
        prev.map(i =>
          i.keyword_id === keywordId ? { ...i, has_pending_serp_request: true } : i
        )
      );
    } catch (err) {
      console.error('Failed to request SERP:', err);
    } finally {
      setRequestingSerpFor(null);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(kw => kw.keyword_id)));
    }
  };

  // A Recon room on the selected keywords. Made empty and opened with the
  // question for its chat, so the room decides what kind of research it is.
  const startRecon = async () => {
    if (startingRecon) return;
    const picked = items.filter(kw => selectedIds.has(kw.keyword_id));
    if (!picked.length) return;
    setStartingRecon(true);
    setReconError(null);
    try {
      const room = await api.createBoardroom({ name: reconName(picked), description: '' });
      shellNavigate(reconRoomPath(room.id, reconQuestion(picked)));
    } catch (e) {
      setReconError(apiErrorMessage(e));
      setStartingRecon(false);
    }
  };

  const fetchPage = useCallback(async (page: number, append: boolean) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    // Only a FRESH load clears the error. An append that clears it first makes
    // a failing retry alternate between the error banner and an empty table
    // several times a second — which is what a retry loop looked like from the
    // outside before the guard below existed.
    if (!append) setError(null);
    try {
      // When semantic query is active, default sort to similarity
      const effectiveSortBy = appliedSemanticQuery && sortBy !== 'similarity' ? 'similarity' : sortBy;

      const result = await api.getTrendingKeywords({
        page,
        size: 50,
        sortBy: effectiveSortBy,
        sortOrder,
        projectId: scope ? null : filterProjectId,
        scope,
        seed: scope === 'explored' ? seedFilter : null,
        minGrowth: minGrowth ? Number(minGrowth) : null,
        minVolume: minVolume ? Number(minVolume) : null,
        keywordSearch: appliedTextFilter || null,
        semanticSearch: appliedSemanticQuery || null,
        minSemSimilarity: appliedSemanticQuery ? appliedMinSemanticSimilarity : null,
        recommendationsOnly: recommendationsOnly || undefined,
        fetchedWithinDays: freshnessDays,
        filed: filedFilter === 'any' ? null : filedFilter,
        hasPage: pageFilter === 'any' ? null : pageFilter,
        gapFilter,
      });
      if (append) {
        setItems(prev => [...prev, ...result.items]);
      } else {
        setItems(result.items);
      }
      setTotal(result.total);
      setHasMore(page < result.pages);
      pageRef.current = page;
    } catch (err: any) {
      setError(err.message || 'Failed to load trending keywords');
      // STOP ASKING. `hasMore` starts true, and a failed first load leaves the
      // table empty — which puts the infinite-scroll sentinel on screen, which
      // fires another fetch, which fails, forever. One transient failure became
      // an unbounded request loop that hammered the API and flashed the section
      // between two states until you navigated away.
      //
      // Cleared again by Retry below, so this ends the loop without ending the
      // list: the user decides when to ask again.
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sortBy, sortOrder, filterProjectId, scope, seedFilter, minGrowth, minVolume, appliedTextFilter, appliedSemanticQuery, appliedMinSemanticSimilarity, recommendationsOnly, freshnessDays, filedFilter, pageFilter, gapFilter]);

  // Load the seed list (Google's explored concepts) when the explored scope is on.
  useEffect(() => {
    if (tab !== 'keywords' || scope !== 'explored') return;
    api.getExploredSeeds().then(setExploredSeeds).catch(() => {});
  }, [scope, tab]);

  // Initial load and when filters/sort change — but only for the tab that
  // shows it. Fetching a table nobody is looking at is a request, a spinner and
  // (as this endpoint proved) an error banner for a screen the reader is not on.
  useEffect(() => {
    if (tab !== 'keywords') return;
    pageRef.current = 1;
    setSelectedIds(new Set());
    fetchPage(1, false);
  }, [fetchPage, tab]);

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          shouldFetchNextPage({
            isIntersecting: entries[0].isIntersecting,
            hasMore,
            error,
            loading,
            loadingMore,
          })
        ) {
          fetchPage(pageRef.current + 1, true);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, error, loading, loadingMore, fetchPage]);

  // Keep the SERP affordance live: flip rows to "pending" when a crawl is
  // requested anywhere, and refresh the list when one completes so the engine
  // icon + date appear without a reload.
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type === 'serp_requested') {
        const ids = new Set(event.keyword_ids);
        setItems(prev =>
          prev.map(i => (ids.has(i.keyword_id) ? { ...i, has_pending_serp_request: true } : i)),
        );
      } else if (event.event_type === 'serp_completion') {
        pageRef.current = 1;
        fetchPage(1, false);
      }
    });
  }, [subscribe, fetchPage]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'keyword' ? 'asc' : 'desc');
    }
  };

  const handleFilterApply = () => {
    setAppliedTextFilter(textFilter);
    setAppliedSemanticQuery(semanticQuery);
    setAppliedMinSemanticSimilarity(minSemanticSimilarity);
    // If semantic query just got activated, switch sort to similarity
    if (semanticQuery && !appliedSemanticQuery) {
      setSortBy('similarity');
      setSortOrder('desc');
    }
    pageRef.current = 1;
    fetchPage(1, false);
  };

  const handleClearFilters = () => {
    setTextFilter('');
    setAppliedTextFilter('');
    setSemanticQuery('');
    setAppliedSemanticQuery('');
    setMinSemanticSimilarity(DEFAULT_MIN_SEM_SIMILARITY);
    setAppliedMinSemanticSimilarity(DEFAULT_MIN_SEM_SIMILARITY);
    if (sortBy === 'similarity') {
      setSortBy('growth');
      setSortOrder('desc');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('keyword_search');
    next.delete('recommendations_only');
    setSearchParams(next, { replace: true });
  };

  /* Everything narrowing the table, not just the two search boxes.
   *
   * It used to mean "text or semantic search is on", which was fine while the
   * panel was always visible. Now that it drives the collapsed panel's "(on)"
   * hint and the empty state's explanation, a scope or recs-only filter it did
   * not count would leave both of those quietly lying about why rows are
   * missing. */
  const hasActiveFilters = !!(
    appliedTextFilter ||
    appliedSemanticQuery ||
    scope ||
    seedFilter ||
    recommendationsOnly ||
    filedFilter !== 'any' ||
    pageFilter !== 'any' ||
    minGrowth ||
    minVolume ||
    gapFilterActive(gapFilter) ||
    filterProjectId
  );

  /* Does ANYTHING on screen have a placement? On a pull of Ads ideas the
     answer is usually no, and a wide column of dashes is worse than no column:
     it takes width from the keyword, which is the thing the row is about.
     Kept rather than dropped because it is the only home for the amber
     recommendation buttons, and /recommendations redirects here for exactly
     that view. */
  const anyFiled = items.some(
    kw => kw.topics.length > 0 || kw.recommended_for_topics.length > 0,
  );

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <span className="text-gray-300 dark:text-gray-600 ml-1">{'\u2195'}</span>;
    return <span className="ml-1">{sortOrder === 'desc' ? '\u25BC' : '\u25B2'}</span>;
  };

  const formatNumber = (n: number | null) => {
    if (n == null) return '-';
    return n.toLocaleString();
  };

  const formatGrowth = (pct: number | null) => {
    if (pct == null) return '-';
    const sign = pct > 0 ? '+' : '';
    pct = Math.round(pct);
    return `${sign}${pct.toLocaleString()}%`;
  };

  return (
    <div>
      {/* A way BACK. You arrive here mid-argument — the room made a claim about search
          demand and you came to check what Google actually associates with the seed.
          Without a return path you land in a dead end and have to navigate home by
          memory, which is enough friction to stop you checking at all. */}

      {/* ONE back button. `?from_brainstorm=` was a second return scheme with a
          second look — an indigo text link that rendered directly above the grey
          chip below whenever both params were present. It went with the
          brainstorm product; the header's single chip is the whole scheme now. */}
      <PageHeader
        /* NAMES THE LIST, not the lobby. The tab strip that used to say which
           of the two you were looking at is now the nav row above, and a
           heading reading "Trends" over one of two tables tells you nothing
           the nav has not already said. */
        title={tab === 'stories' ? 'Trending now' : 'Recent keywords'}
        subtitle={
          tab === 'stories'
            ? 'What the country is searching today, researched into pages you could build.'
            : 'Keywords you have pulled from Google Ads, freshest first.'
        }
        className="mb-4"
        /* WHICH MODELS BILL HERE. Researching a trend spends on a model, and
           the only way to know which — or to go and change it — was to read the
           agent code. The backend owns the list (stage_surfaces."trends"), so it
           stays right when a stage is added rather than drifting like the
           hand-rolled chips this widget replaced.

           On the page header rather than inside a tab: it describes the screen,
           and both tabs bill. */
        right={<StageModelsWidget surface="trends" returnLabel="Trends" />}
      />

      {/* Mounted only when selected — the stories tab kicks off research as a
          side effect of being read, and someone who came for their own keywords
          did not ask to spend Google Ads and GNews quota. */}
      {tab === 'stories' && <StoriesWorthAPage />}

      {tab === 'keywords' && (
      <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Google Ads growth for terms you have pulled, freshest first. Old pulls fade out.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={freshnessDays ?? ''}
            onChange={e => setFreshnessDays(e.target.value === '' ? null : Number(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {FRESHNESS_WINDOWS.map(w => (
              <option key={w.label} value={w.days ?? ''}>{w.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowFilters(v => !v)}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Filters {hasActiveFilters ? '(on)' : ''} {showFilters ? '▾' : '▸'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 mb-4 ${showFilters ? '' : 'hidden'}`}>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Scope</label>
            <select
              value={scope ?? (filterProjectId ?? '')}
              onChange={e => {
                const v = e.target.value;
                if (v === 'explored') {
                  setScope(v);
                  setFilterProjectId(null);
                } else {
                  setScope(null);
                  setFilterProjectId(v ? Number(v) : null);
                }
                if (v !== 'explored') setSeedFilter(null);
              }}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All Subjects</option>
              <option value="explored">Explored (all Google Ads pulls)</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {scope === 'explored' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Google associates with…
              </label>
              <select
                value={seedFilter ?? ''}
                onChange={e => setSeedFilter(e.target.value || null)}
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[16rem]"
              >
                <option value="">All explored seeds</option>
                {exploredSeeds.map(s => (
                  <option key={s.seed} value={s.seed}>
                    {s.seed} ({s.keyword_count})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-end">
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={recommendationsOnly}
                onChange={e => setRecommendationsOnly(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
              />
              Recs only
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Min Growth %</label>
            <input
              type="number"
              value={minGrowth}
              onChange={e => setMinGrowth(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleFilterApply()}
              placeholder="e.g. 20"
              className="w-24 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Min Volume</label>
            <input
              type="number"
              value={minVolume}
              onChange={e => setMinVolume(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleFilterApply()}
              placeholder="e.g. 100"
              className="w-24 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          {/* WHAT HAS ALREADY BEEN DONE WITH IT. The "Filed under" and "Page"
              columns showed this but could not act on it — on a pull of 400 Ads
              ideas the rows worth looking at are the ones nothing has happened
              to yet, and finding them meant scrolling past everything that had. */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Filed</label>
            <select
              value={filedFilter}
              onChange={e => setFiledFilter(e.target.value as typeof filedFilter)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="any">Filed or not</option>
              <option value="unfiled">Not filed yet</option>
              <option value="filed">Already filed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Page</label>
            <select
              value={pageFilter}
              onChange={e => setPageFilter(e.target.value as typeof pageFilter)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="any">Written or not</option>
              <option value="no">No page yet</option>
              <option value="yes">Has a page</option>
            </select>
          </div>
          <SerpGapFilterFields value={gapFilter} onChange={setGapFilter} onEnter={handleFilterApply} />
          <button
            onClick={handleFilterApply}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Apply
          </button>
        </div>

        {/* Text & Semantic Search */}
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-3">
          {/* Text Search */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Text search
            </label>
            <input
              type="text"
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
              placeholder='e.g., "horse" -"mountain biking" -mtb'
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && handleFilterApply()}
            />
          </div>

          {/* Semantic Query */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Semantic sort query
              <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">(sort by similarity)</span>
            </label>
            <input
              type="text"
              value={semanticQuery}
              onChange={(e) => setSemanticQuery(e.target.value)}
              placeholder="e.g., horse riding"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && handleFilterApply()}
            />
            {/* Semantic Threshold Slider */}
            <div className="mt-2 flex items-center gap-3">
              <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Min similarity{minSemanticSimilarity > 0 ? ` to '${semanticQuery || "your query"}': ${Math.round(minSemanticSimilarity * 100)}%` : ': off (sort only)'}
              </label>
              <input
                type="range"
                min="0"
                max="0.95"
                step="0.05"
                value={minSemanticSimilarity}
                onChange={(e) => setMinSemanticSimilarity(parseFloat(e.target.value))}
                className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          </div>

          {/* Clear filters link */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleClearFilters}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Clear text/semantic filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {appliedTextFilter && (
            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs rounded-full">
              text: {appliedTextFilter}
            </span>
          )}
          {appliedSemanticQuery && (
            <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs rounded-full">
              semantic: {appliedSemanticQuery} ({Math.round(appliedMinSemanticSimilarity * 100)}%+)
            </span>
          )}
        </div>
      )}

      {/* Error. Carries its own way out: the fetch stops retrying on failure
          (see fetchPage), so without a button here a hiccup would leave the
          section permanently empty. */}
      {error && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <span>{error}</span>
          <button
            onClick={() => {
              setHasMore(true);
              pageRef.current = 1;
              fetchPage(1, false);
            }}
            disabled={loading}
            className="rounded border border-red-300 px-2 py-1 text-xs font-medium hover:bg-red-100 disabled:opacity-50 dark:border-red-700 dark:hover:bg-red-900/50"
          >
            {loading ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="mb-2 flex items-center gap-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
            {selectedIds.size} keyword{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          {/* The verb that produces something. "Add to subject" files a keyword
              away; this turns a pile of researched terms into a subject with a
              planned structure, which is what you were going to do next. */}
          <button
            onClick={() => setShowSubjectModal(true)}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Start a subject
          </button>
          <button
            onClick={() => setShowBulkModal(true)}
            className="px-3 py-1 text-sm border border-blue-600 text-blue-700 dark:text-blue-300 dark:border-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40"
          >
            Add to subject
          </button>
          {/* Only for people who have Recon: creating a room needs it. */}
          {held.includes('BOARDROOM') && (
            <button
              onClick={() => void startRecon()}
              disabled={startingRecon}
              title="Open a Recon room on these keywords: what searchers want, who serves them, and what is missing"
              className="px-3 py-1 text-sm border border-indigo-500 text-indigo-600 dark:text-indigo-300 dark:border-indigo-400 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-50"
            >
              {startingRecon ? 'Opening…' : 'Start a recon'}
            </button>
          )}
          {reconError && <span className="text-xs text-red-600 dark:text-red-400">{reconError}</span>}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-2 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <th className="w-8 px-2 py-2.5">
                <input
                  type="checkbox"
                  checked={items.length > 0 && selectedIds.size === items.length}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                />
              </th>
              <th
                className="text-left px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                onClick={() => handleSort('keyword')}
              >
                Keyword <SortIcon field="keyword" />
              </th>
              {/* The SERP action, out of the Keyword cell. Hidden on small
                  screens like the other secondary columns — a phone has no room
                  for it and the keyword needs every pixel there most. */}
              <th className="hidden md:table-cell text-left px-2 py-2.5 font-medium text-gray-700 dark:text-gray-300 w-[130px]">
                SERP
              </th>
              {appliedSemanticQuery && (
                <th
                  className="hidden sm:table-cell text-right px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none w-[80px]"
                  onClick={() => handleSort('similarity')}
                >
                  Sim <SortIcon field="similarity" />
                </th>
              )}
              <th className="text-center px-1 sm:px-2 py-2.5 font-medium text-gray-700 dark:text-gray-300 w-[80px] sm:w-[110px]">
                Trend
              </th>
              <th
                className="hidden sm:table-cell text-right px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                onClick={() => handleSort('avg_monthly_searches')}
              >
                LT Avg <SortIcon field="avg_monthly_searches" />
              </th>
              <th
                className="hidden sm:table-cell text-right px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                onClick={() => handleSort('recent_avg')}
              >
                3mo Avg <SortIcon field="recent_avg" />
              </th>
              <th
                className="text-right px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                onClick={() => handleSort('growth')}
              >
                Growth % <SortIcon field="growth" />
              </th>
              <th className="hidden md:table-cell text-center px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300">Competition</th>
              <th
                className="hidden md:table-cell text-center px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300 cursor-help"
                title={SERP_GAP_HELP}
              >
                Open positions
              </th>
              <th
                className={`hidden lg:table-cell text-left py-2.5 font-medium text-gray-700 dark:text-gray-300 ${
                  anyFiled ? 'px-3' : 'px-1 w-8'
                }`}
                title={anyFiled ? undefined : 'Nothing here is filed in a subject yet'}
              >
                {anyFiled ? 'Filed under' : ''}
              </th>
              <th className="hidden md:table-cell text-center px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300">Page</th>
              <th className="text-center px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={appliedSemanticQuery ? 14 : 13}>
                  <LoadingRegion label="Loading keywords…" />
                </td>
              </tr>
            ) : !loading && items.length === 0 ? (
              <tr>
                <td colSpan={appliedSemanticQuery ? 14 : 13} className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {(() => {
                    const reason = emptyReason({ windowDays: freshnessDays, hasFilters: hasActiveFilters });
                    return (
                      <div className="space-y-2">
                        <p>{emptyMessage(reason, freshnessDays)}</p>
                        {/* The window is the likeliest cause and lives outside
                            the Filters disclosure, so widening it has to be
                            reachable from here rather than described. */}
                        {(reason === 'window' || reason === 'both') && (
                          <button
                            onClick={() => setFreshnessDays(null)}
                            className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                          >
                            Show keywords of any age
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </td>
              </tr>
            ) : (
              items.map(kw => (
                <tr key={kw.keyword_id} className={`border-b border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:opacity-100 ${fadeClass(ageBucket(kw.collected_at, freshnessDays))} ${selectedIds.has(kw.keyword_id) ? 'bg-blue-50 dark:bg-blue-900/30' : 'odd:bg-white dark:odd:bg-gray-800 even:bg-gray-50 dark:even:bg-gray-800 dark:even:bg-gray-900'}`}>
                  <td className="w-8 px-2 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(kw.keyword_id)}
                      onChange={() => toggleSelect(kw.keyword_id)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                    />
                  </td>
                  {/* THE KEYWORD, and only the keyword.
                      It used to share a 240px cell with the SERP affordance,
                      an external-search icon and the age chip — four things
                      competing, and the one the row is ABOUT lost, so nearly
                      every keyword rendered as "padres v b…". The SERP controls
                      are an action on the keyword, not part of its name, so
                      they have their own column now and this one is free to use
                      the width the table has. */}
                  <td
                    className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100"
                    title={kw.keyword_value}
                  >
                    <div className="flex items-baseline gap-1.5">
                      {/* Wraps rather than truncates: a long-tail keyword is
                          often long precisely BECAUSE the tail is the
                          interesting part, and hiding the end of it hides what
                          distinguishes it from the row above. */}
                      <span className="break-words">{kw.keyword_value}</span>
                      <span
                        className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] ${ageChipClass(ageBucket(kw.collected_at, freshnessDays))}`}
                        title={kw.collected_at ? `Pulled from Google Ads ${new Date(kw.collected_at).toLocaleDateString()}` : 'No record of when this was pulled'}
                      >
                        {ageLabel(kw.collected_at)}
                      </span>
                    </div>
                    <span className="sm:hidden text-xs text-gray-500 dark:text-gray-400 font-normal">
                      LT Avg: {formatNumber(kw.avg_monthly_searches)}
                      {kw.competition_level && <> · {kw.competition_level}</>}
                    </span>
                  </td>

                  {/* SERP: fetch it, or open what was fetched. An action, so it
                      sits with the other per-row controls rather than inside
                      the name. */}
                  <td className="hidden md:table-cell px-2 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {kw.latest_serp_date ? (
                        <button
                          onClick={() => setSerpModalKeyword({ id: kw.keyword_id, value: kw.keyword_value })}
                          className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                          title={`View ${kw.serp_count} SERP result${kw.serp_count !== 1 ? 's' : ''}`}
                        >
                          <EngineIcon engine={kw.latest_serp_engine} size="sm" />
                          <span>{formatSerpDate(kw.latest_serp_date)}</span>
                        </button>
                      ) : kw.has_pending_serp_request ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                          <RefreshGlyph spinning /><span>Pending</span>
                        </span>
                      ) : (
                        <button
                          onClick={() => handleRequestSerp(kw.keyword_id)}
                          disabled={requestingSerpFor === kw.keyword_id || !networkEnabled}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          title={networkEnabled ? 'Fetch SERP' : 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'}
                        >
                          {requestingSerpFor === kw.keyword_id ? '...' : 'Fetch SERP'}
                        </button>
                      )}
                      <ExternalSearchLink engine={kw.latest_serp_engine} keyword={kw.keyword_value} />
                    </div>
                  </td>

                  {appliedSemanticQuery && (
                    <td className="hidden sm:table-cell px-3 py-2 text-right text-sm">
                      {kw.semantic_similarity != null ? (
                        <span className={`font-medium ${
                          kw.semantic_similarity >= 0.8 ? 'text-green-600 dark:text-green-400' :
                          kw.semantic_similarity >= 0.6 ? 'text-yellow-600 dark:text-yellow-400' : 'text-orange-600 dark:text-orange-400'
                        }`}>
                          {Math.round(kw.semantic_similarity * 100)}%
                        </span>
                      ) : '-'}
                    </td>
                  )}
                  <td className="px-1 sm:px-2 py-2">
                    <div className="flex justify-center">
                      <LineChart width={80} height={28} data={kw.monthly_data}>
                        <Tooltip
                          offset={0}
                          position={{ y: -30 }}
                          content={({ active, payload }) => {
                            if (!active || !payload?.[0]) return null;
                            const d = payload[0].payload;
                            return (
                              <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">
                                {d.month} {d.year}: {Number(d.searches).toLocaleString()}
                              </div>
                            );
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="searches"
                          stroke={kw.growth_pct != null && kw.growth_pct > 0 ? '#16a34a' : '#dc2626'}
                          dot={false}
                          strokeWidth={1.5}
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                    {formatNumber(kw.avg_monthly_searches)}
                  </td>
                  <td className="hidden sm:table-cell px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                    {Math.round(kw.recent_avg).toLocaleString()}
                  </td>
                  <td className={`px-3 py-2 text-right font-medium ${
                    kw.growth_pct != null && kw.growth_pct > 0
                      ? 'text-green-600 dark:text-green-400'
                      : kw.growth_pct != null && kw.growth_pct < 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {formatGrowth(kw.growth_pct)}
                  </td>
                  <td className="hidden md:table-cell px-3 py-2 text-center">
                    {kw.competition_level ? (
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        kw.competition_level === 'LOW' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                        kw.competition_level === 'MEDIUM' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
                        kw.competition_level === 'HIGH' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}>
                        {kw.competition_level}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="hidden md:table-cell px-3 py-2 text-center">
                    {kw.serp_gaps?.some(g => g.positions.length > 0)
                      ? <SerpGapBadge gaps={kw.serp_gaps} />
                      : <span className="text-gray-400 dark:text-gray-500">-</span>}
                  </td>
                  <td
                    className={`hidden lg:table-cell py-2 text-sm text-gray-600 dark:text-gray-300 ${
                      anyFiled ? 'px-3 max-w-[200px]' : 'px-1 w-8 text-center'
                    }`}
                  >
                    {(() => {
                      const recs = kw.recommended_for_topics.filter(rt => !kw.topics.some(t => t.topic_id === rt.topic_id));
                      const total = kw.topics.length + recs.length;
                      if (total === 0) return '-';
                      let idx = 0;
                      return (
                        <span>
                          {kw.topics.map(t => (
                            <span key={`t-${t.topic_id}`}>
                              <Link
                                to={`/subjects/${t.project_id}?node=${t.topic_id}&highlight_keyword=${kw.keyword_id}`}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline whitespace-nowrap"
                              >
                                {t.topic_name}
                              </Link>
                              {++idx < total && <span className="text-gray-400 dark:text-gray-500">, </span>}
                            </span>
                          ))}
                          {recs.map(rt => (
                            <span key={`rec-${rt.rec_topic_keyword_id}`}>
                              <button
                                onClick={() => setRecModal({ keyword: kw, rec: rt })}
                                className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 whitespace-nowrap"
                                title="Add to this recommended topic"
                              >
                                +{rt.topic_name}
                              </button>
                              {++idx < total && <span className="text-gray-400 dark:text-gray-500">, </span>}
                            </span>
                          ))}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="hidden md:table-cell px-3 py-2 text-center">
                    {kw.has_managed_page ? (
                      <span className="text-green-600 dark:text-green-400" title="Has managed page">&#10003;</span>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">-</span>
                    )}
                  </td>
                  <td className="px-2 sm:px-3 py-2 text-center">
                    <button
                      onClick={() => setModalKeyword(kw)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Add to subject</span>
                      <span className="sm:hidden">+ Subject</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="h-4" />
      {loadingMore && (
        <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">Loading more...</div>
      )}
      {!hasMore && items.length > 0 && (
        <div className="text-center py-4 text-sm text-gray-400 dark:text-gray-500">
          Showing all {total.toLocaleString()} keywords
        </div>
      )}
      </>
      )}

      {/* The modals stay outside the tabs. A create started on one tab should
          not be cancelled by the tab switch behind it. */}

      {/* Add-to-subject modal (single keyword) */}
      {modalKeyword && (
        <AddToTopicModal
          keywords={[{ id: modalKeyword.keyword_id, value: modalKeyword.keyword_value }]}
          initialProjectId={filterProjectId}
          onClose={() => setModalKeyword(null)}
          onSuccess={() => {
            setModalKeyword(null);
            pageRef.current = 1;
            fetchPage(1, false);
          }}
        />
      )}

      {/* Add-to-subject modal (from a recommended node) */}
      {recModal && (
        <AddToTopicModal
          keywords={[{ id: recModal.keyword.keyword_id, value: recModal.keyword.keyword_value }]}
          initialProjectId={recModal.rec.project_id}
          initialCategoryId={recModal.rec.category_id}
          initialTopicId={recModal.rec.topic_id}
          onClose={() => setRecModal(null)}
          onSuccess={() => {
            setRecModal(null);
            pageRef.current = 1;
            fetchPage(1, false);
          }}
        />
      )}

      {/* Add-to-subject modal (bulk) */}
      {showBulkModal && (
        <AddToTopicModal
          keywords={items
            .filter(kw => selectedIds.has(kw.keyword_id))
            .map(kw => ({ id: kw.keyword_id, value: kw.keyword_value }))}
          initialProjectId={filterProjectId}
          onClose={() => setShowBulkModal(false)}
          onSuccess={() => {
            setShowBulkModal(false);
            setSelectedIds(new Set());
            pageRef.current = 1;
            fetchPage(1, false);
          }}
        />
      )}

      {/* Start a subject from the selected keywords */}
      {showSubjectModal && (
        <StartSubjectModal
          keywords={items.filter(kw => selectedIds.has(kw.keyword_id))}
          onClose={() => setShowSubjectModal(false)}
        />
      )}

      {/* SERP results slideout */}
      {serpModalKeyword && (
        <SerpModal
          isOpen={true}
          onClose={() => setSerpModalKeyword(null)}
          keywordId={serpModalKeyword.id}
          keywordValue={serpModalKeyword.value}
        />
      )}
    </div>
  );
}
